import AutorenewIcon from '@mui/icons-material/Autorenew';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Grid, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMixpanel } from 'react-mixpanel-browser';

import { ShortDeviceResponse } from '@/api/devices';
import { useApiToken, useGenerateApiToken } from '@/api/sofarApi';
import { unAuthAxios } from '@/common/axios';
import { FormInputText } from '@/components/form/FormInputText';
import { cardBgLightGrey } from '@/theme';

export const AuthenticationSection = () => {
    const { data: apiTokenData } = useApiToken();
    const { refetch: generateToken } = useGenerateApiToken({ config: { enabled: false } });
    const mixpanel = useMixpanel();
    const { control, reset } = useForm<{ token: string }>({
        defaultValues: { token: '' },
        mode: 'onChange',
    });
    const [testRequestResult, setTestRequestResult] = useState<{
        status: string;
        data?: ShortDeviceResponse | { message: string };
    }>({
        status: 'idle',
    });

    const handleGenerateToken = async () => {
        try {
            const newToken = await generateToken();
            mixpanel?.track('Generate API Token');
            reset({ token: newToken?.data?.data.token });
        } catch (err) {
            console.error(err);
        }
    };

    const onTestRequest = async () => {
        try {
            setTestRequestResult({ status: 'loading' });
            const data = await unAuthAxios.get<ShortDeviceResponse, ShortDeviceResponse>('/api/devices', {
                headers: {
                    token: apiTokenData?.data.token,
                },
            });
            setTestRequestResult({ status: 'success', data });
        } catch (err) {
            setTestRequestResult({ status: 'error', data: { message: (err as Error).message } });
            console.error(err);
        }
    };

    const onAccordionChange = (e: React.SyntheticEvent, isExpanded: boolean) => {
        if (isExpanded) {
            return;
        }
        setTestRequestResult({ status: 'idle' });
    };

    useEffect(() => {
        reset({ token: apiTokenData?.data.token });
    }, [apiTokenData?.data.token, reset]);

    return (
        <>
            <Grid size={{ xs: 12 }}>
                <Typography variant="h4" gutterBottom>
                    Authentication
                </Typography>
            </Grid>
            <Grid container spacing={6}>
                <Grid size={{ xs: 12, md: 7 }}>
                    <Typography variant="body1" gutterBottom>
                        To get started using the Sofar API, first generate an authentication token, which gives you
                        access to the Spotters and data owned by your account via the API. You must include this token
                        in a <strong>token</strong> field in the request headers for all API requests. You can
                        regenerate the authentication token at any time, but this will invalidate any previous tokens.
                        API requests must be made over HTTPS.
                    </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 8, md: 5 }}>
                    <Stack spacing={2}>
                        <FormInputText
                            name="token"
                            control={control}
                            label="Your API Token"
                            inputFieldProps={{
                                id: 'token',
                                type: 'text',
                                size: 'medium',
                                disabled: true,
                                sx: {
                                    '&& .MuiOutlinedInput-notchedOutline.MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'info.main',
                                    },
                                    '&& .MuiFormLabel-root.Mui-disabled': {
                                        color: 'info.main',
                                    },
                                    '&& .MuiOutlinedInput-input.Mui-disabled': {
                                        WebkitTextFillColor: 'black',
                                    },
                                },
                            }}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button variant="outlined" color="success" onClick={handleGenerateToken}>
                                Generate New Token
                            </Button>
                        </Box>
                    </Stack>
                </Grid>
            </Grid>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                    <Accordion onChange={onAccordionChange}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="h4">Details and examples</Typography>
                        </AccordionSummary>
                        <AccordionDetails
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                            }}
                        >
                            <Typography variant="body1" gutterBottom>
                                This token is generated by and valid for Sofar API located at:
                            </Typography>
                            <Typography
                                variant="body1"
                                gutterBottom
                                sx={{
                                    backgroundColor: cardBgLightGrey,
                                    p: 1,
                                    borderRadius: '4px',
                                    fontFamily: 'monospace',
                                }}
                            >
                                {process.env.REACT_APP_API_URL}
                            </Typography>
                            <Typography variant="body1">Example request:</Typography>
                            <Typography variant="caption" gutterBottom>
                                This example will work for macOS and Linux. Windows &quot;curl&quot; uses different
                                syntax.
                            </Typography>
                            <Typography
                                component="code"
                                sx={{
                                    backgroundColor: cardBgLightGrey,
                                    p: 1,
                                    borderRadius: '4px',
                                    fontFamily: 'monospace',
                                }}
                                data-testid="example-request-code-section"
                            >
                                {`curl ${process.env.REACT_APP_API_URL}/api/devices -H 'token: ${apiTokenData?.data.token}'`}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    variant="contained"
                                    onClick={onTestRequest}
                                    disabled={testRequestResult.status === 'loading'}
                                >
                                    Send Test Request
                                </Button>
                            </Box>
                            {testRequestResult.status !== 'idle' && (
                                <>
                                    <Typography variant="body1" gutterBottom>
                                        Request result
                                        {testRequestResult.status === 'success' && (
                                            <CheckCircleOutlineIcon
                                                sx={{ position: 'relative', top: 3, mx: 1 }}
                                                color="success"
                                            />
                                        )}
                                        {testRequestResult.status === 'error' && (
                                            <ErrorOutlineIcon
                                                sx={{ position: 'relative', top: 3, mx: 1 }}
                                                color="error"
                                            />
                                        )}
                                        {testRequestResult.status === 'loading' && (
                                            <AutorenewIcon sx={{ position: 'relative', top: 3, mx: 1 }} color="info" />
                                        )}
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        gutterBottom
                                        component="pre"
                                        sx={{
                                            backgroundColor: cardBgLightGrey,
                                            p: 1,
                                            borderRadius: '4px',
                                            fontFamily: 'monospace',
                                            maxHeight: '300px',
                                            overflow: 'scroll',
                                        }}
                                        data-testid="example-request-result-section"
                                    >
                                        {JSON.stringify(testRequestResult.data, null, 2)}
                                    </Typography>
                                </>
                            )}
                        </AccordionDetails>
                    </Accordion>
                </Grid>
            </Grid>
        </>
    );
};
